/**
 * RoleContractEditor — J7-W2 task 2 (refactored BD-3 A.1)
 *
 * Orchestrator: holds shared state, data-fetching, and save/activate handlers.
 * Tab content is rendered by dedicated section components in ./sections/.
 * State types, constants, and hydrate/toPayload helpers live in
 * ./roleContractEditorState.ts (BD-3 A.2).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { Card, CardContent } from '@/components/ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { showToast } from '@/components/ui/Toast';
import { ScrollText, CheckCircle2, Circle, Save } from 'lucide-react';
import { t } from '@/lib/i18n';
import {
  useRorosRoleContract,
  useRorosRoleContractCreate,
  useRorosRoleContractUpdate,
  useRorosRoleContractActivate,
} from '@/hooks/roros/role-contracts';
import { useRorosActors } from '@/hooks/roros/actors';
import { useRorosAllMetricsForOrg } from '@/hooks/roros/artifacts';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { MultiSelectOption } from '@/components/ui/MultiSelect';
import {
  type EditorState,
  COMPONENT_TABS,
  COMPONENT_KEYS,
  COMPONENT_LABELS,
  emptyState,
  hydrate,
  toPayload,
} from './roleContractEditorState';

// ─── Section components ───────────────────────────────────────────────────────
import { PurposeSection } from './sections/PurposeSection';
import { OutcomesSection } from './sections/OutcomesSection';
import { RoutineOwnershipSection } from './sections/RoutineOwnershipSection';
import { DecisionRightsSection } from './sections/DecisionRightsSection';
import { ToolAccessPolicySection } from './sections/ToolAccessPolicySection';
import { QcDutiesSection } from './sections/QcDutiesSection';
import { InterfacesSection } from './sections/InterfacesSection';
import { UpgradeRulesSection } from './sections/UpgradeRulesSection';

export function RoleContractEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const contractQuery = useRorosRoleContract(isNew ? '' : id);
  const createMutation = useRorosRoleContractCreate();
  const updateMutation = useRorosRoleContractUpdate();
  const activateMutation = useRorosRoleContractActivate();
  const actorsQuery = useRorosActors();
  const metricsQuery = useRorosAllMetricsForOrg();

  const [state, setState] = useState<EditorState>(() => emptyState());
  const [activeTab, setActiveTab] = useState<string>('purpose');
  const [contractId, setContractId] = useState<string | null>(isNew ? null : id ?? null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);

  useEffect(() => {
    if (!isNew && contractQuery.data) {
      setState(hydrate(contractQuery.data));
      setContractId(contractQuery.data.id);
    }
  }, [isNew, contractQuery.data]);

  const actorOptions: MultiSelectOption[] = useMemo(() => {
    return ((actorsQuery.data as RorosActorDTO[] | undefined) ?? []).map((a) => ({
      value: a.id,
      label: a.name,
      description: a.actorType,
    }));
  }, [actorsQuery.data]);

  // Sven dogfood 2026-05-03: the Interfaces (now "Reports & Collaboration")
  // dropdowns are about reporting / collaboration / escalation lines —
  // strictly humans + AI Operators. Knowledge Personas are advisory
  // entities; they are not in the org chart, so they don't belong in
  // these pickers. Pre-fix the same `actorOptions` was reused for
  // Purpose tab (linked actor) AND Interfaces; now we derive a
  // narrower list for Interfaces.
  const interfacesActorOptions: MultiSelectOption[] = useMemo(() => {
    return ((actorsQuery.data as RorosActorDTO[] | undefined) ?? [])
      .filter((a) => a.actorType !== 'knowledge_persona')
      .map((a) => ({
        value: a.id,
        label: a.name,
        description: a.actorType,
      }));
  }, [actorsQuery.data]);

  const metricsData = useMemo(() => (metricsQuery.data ?? []) as Array<{ id: string; title?: string; name?: string }>, [metricsQuery.data]);

  const metricOptions: MultiSelectOption[] = useMemo(() => {
    return metricsData.map((m) => ({
      value: m.id,
      label: m.title ?? m.name ?? m.id,
    }));
  }, [metricsData]);

  const componentStatus = useMemo(() => ({
    purpose_scope: state.purposeScope.trim().length > 0,
    outcomes_ownership: state.outcomesOwnership.length > 0,
    routine_ownership: state.routineOwnership.length > 0,
    decision_rights: Object.values(state.decisionRights).some((v) => !!v),
    tool_access_policy: state.toolGrants.length > 0 || state.toolBudgetCap.trim() !== '',
    qc_duties: state.qcCanPerform || state.qcTypes.length > 0 || state.qcChecklist.trim() !== '',
    interfaces: state.reportingTo.length > 0 || state.collaboratesWith.length > 0 || state.escalatesTo.length > 0,
    upgrade_rules: state.upgradeAutoSuggest.length > 0 || state.upgradeRequiresCeo.length > 0 || state.upgradeMaxPerMonth.trim() !== '',
  }), [state]);

  const completedCount = Object.values(componentStatus).filter(Boolean).length;
  const allComponentsComplete = completedCount === COMPONENT_KEYS.length;

  const handleSave = (then?: 'activate') => {
    setSaveError(null);
    setActivateError(null);
    if (!state.name.trim()) {
      setSaveError(t('Name is required.'));
      return;
    }
    const payload = toPayload(state);
    if (isNew && !contractId) {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          setContractId(created.id);
          showToast.success(t('Role Contract created'));
          if (then === 'activate') {
            activateMutation.mutate(created.id, {
              onSuccess: () => {
                showToast.success(t('Role Contract activated'));
                navigate(`/roros/role-contracts/${created.id}`);
              },
              onError: (err) => setActivateError(err instanceof Error ? err.message : t('Activation failed')),
            });
          } else {
            navigate(`/roros/role-contracts/${created.id}/edit`, { replace: true });
          }
        },
        onError: (err) => setSaveError(err instanceof Error ? err.message : t('Save failed')),
      });
    } else {
      const targetId = contractId ?? id!;
      // J7-W2 fix-wave 2 (B8): actor_id accepted on update (see updateContract schema).
      updateMutation.mutate(
        { id: targetId, payload },
        {
          onSuccess: () => {
            showToast.success(t('Role Contract saved'));
            if (then === 'activate') {
              activateMutation.mutate(targetId, {
                onSuccess: () => {
                  showToast.success(t('Role Contract activated'));
                  navigate(`/roros/role-contracts/${targetId}`);
                },
                onError: (err) => setActivateError(err instanceof Error ? err.message : t('Activation failed')),
              });
            }
          },
          onError: (err) => setSaveError(err instanceof Error ? err.message : t('Save failed')),
        },
      );
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isActivating = activateMutation.isPending;

  return (
    <RorosPage
      title={isNew ? t('New Role Contract') : t('Edit Role Contract')}
      icon={<ScrollText className="h-5 w-5" />}
      breadcrumbs={[{ label: t('Role Contracts'), path: '/roros/role-contracts' }]}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleSave()}
            disabled={isSaving}
            leftIcon={<Save className="h-4 w-4" />}
            data-testid="role-contract-save-button"
          >
            {isSaving ? t('Saving...') : t('Save Draft')}
          </Button>
          <Button
            onClick={() => handleSave('activate')}
            disabled={isSaving || isActivating || !allComponentsComplete}
            title={!allComponentsComplete ? t('All 8 components required for activation (GOV-06).') : undefined}
            leftIcon={<CheckCircle2 className="h-4 w-4" />}
            data-testid="role-contract-save-activate-button"
          >
            {isActivating ? t('Activating...') : t('Save & Activate')}
          </Button>
        </div>
      }
    >
      <div className="mt-6 space-y-4">
        {(saveError || activateError) && (
          <Alert variant="error" data-testid="role-contract-error">
            {saveError || activateError}
          </Alert>
        )}

        {/*
          Sven dogfood 2026-05-03: pre-fix the progress card showed
          "GOV-06 progress: 4/8 components complete" + a row of raw
          enum keys (purpose_scope, outcomes_ownership, …) that no
          founder could read. Replaced with a human label + ✓/○
          icons + readable section names. The GOV-06 reference moved
          into a small caption so compliance auditors still see it.
        */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-baseline justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t('Role contract setup')}
                    {' — '}
                    <span className="text-primary-700">{completedCount}</span>
                    {' '}
                    {t('of')}
                    {' '}
                    {COMPONENT_KEYS.length}
                    {' '}
                    {t('sections complete')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('Each section captures part of the role agreement (PRD GOV-06).')}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {COMPONENT_KEYS.map((k) => {
                  const done = (componentStatus as Record<string, boolean>)[k];
                  return (
                    <Badge
                      key={k}
                      variant="outline"
                      className={`text-xs flex items-center gap-1 ${done ? 'bg-green-50 border-green-300 text-green-700' : 'bg-muted/30 text-muted-foreground'}`}
                    >
                      {done
                        ? <CheckCircle2 className="h-3 w-3" aria-hidden />
                        : <Circle className="h-3 w-3" aria-hidden />
                      }
                      {COMPONENT_LABELS[k] ?? k}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Sven dogfood 2026-05-03: tabs were centered by the row's
              default layout — left-align so they read as a normal
              navigation strip. */}
          <TabsList className="flex flex-wrap justify-start gap-1">
            {COMPONENT_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="purpose" className="mt-4">
            <PurposeSection state={state} setState={setState} actorOptions={actorOptions} />
          </TabsContent>

          <TabsContent value="outcomes" className="mt-4">
            <OutcomesSection state={state} setState={setState} metricOptions={metricOptions} metricsData={metricsData} />
          </TabsContent>

          <TabsContent value="routines" className="mt-4">
            <RoutineOwnershipSection state={state} setState={setState} />
          </TabsContent>

          <TabsContent value="decisions" className="mt-4">
            <DecisionRightsSection state={state} setState={setState} />
          </TabsContent>

          <TabsContent value="tools" className="mt-4">
            <ToolAccessPolicySection state={state} setState={setState} contractId={contractId} />
          </TabsContent>

          <TabsContent value="qc" className="mt-4">
            <QcDutiesSection state={state} setState={setState} />
          </TabsContent>

          <TabsContent value="interfaces" className="mt-4">
            <InterfacesSection state={state} setState={setState} actorOptions={interfacesActorOptions} />
          </TabsContent>

          <TabsContent value="upgrades" className="mt-4">
            <UpgradeRulesSection state={state} setState={setState} />
          </TabsContent>
        </Tabs>
      </div>
    </RorosPage>
  );
}
